import React, { useContext, useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { AppContext } from '../../App';
import LabDataLayout, { useLabDataFilters } from '../../components/LabData/LabDataLayout';
import LabDataKpiCard from '../../components/LabData/LabDataKpiCard';
import LabDataWarningsBanner from '../../components/LabData/LabDataWarningsBanner';
import SpendOverTimeChart from '../../components/LabData/SpendOverTimeChart';
import SpendBySupplierTable from '../../components/LabData/SpendBySupplierTable';
import {
  getSpendByPeriod,
  getSpendBySupplier,
  getSpendSummary,
} from '../../clients/lab_data_client';
import { formatNisAmount } from '../../utils/labData';
import { showToast } from '../../config_and_helpers/helpers';
import { logInfo } from '../../config_and_helpers/logger';

const LOG_CONTEXT = 'LabDataSpendPage';

const LabDataSpendContent = () => {
  const { token } = useContext(AppContext);
  const filters = useLabDataFilters();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [warnings, setWarnings] = useState([]);

  useEffect(() => {
    if (!token || !filters) return;

    let cancelled = false;
    setLoading(true);
    logInfo(LOG_CONTEXT, 'load spend data', filters);

    const options = {
      from: filters.from,
      to: filters.to,
      budgetLineId: filters.budgetLineId,
      projectId: filters.projectId,
      supplierId: filters.supplierId,
    };

    Promise.all([
      getSpendSummary(token, options),
      getSpendByPeriod(token, options),
      getSpendBySupplier(token, options),
    ]).then(([summaryRes, periodRes, supplierRes]) => {
      if (cancelled) return;
      setLoading(false);

      if (!summaryRes.success) {
        showToast(
          summaryRes.messages?.[0] || 'Failed to load spend summary',
          'error',
          'top-right',
          5000
        );
        return;
      }

      setSummary(summaryRes.data);
      setWarnings(summaryRes.data?.warnings ?? []);
      setPeriods(periodRes.success ? periodRes.data?.data?.periods ?? [] : []);
      setSuppliers(
        supplierRes.success ? supplierRes.data?.data?.suppliers ?? [] : []
      );
    });

    return () => {
      cancelled = true;
    };
  }, [token, filters]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  const data = summary?.data ?? {};

  return (
    <>
      <LabDataWarningsBanner warnings={warnings} />
      <Grid container spacing={2} className="lab-data-kpi-grid">
        <Grid item xs={12} sm={4}>
          <LabDataKpiCard
            label="Total spend"
            value={formatNisAmount(data.total_nis)}
            subtitle={`${data.order_count ?? 0} orders · ${data.line_count ?? 0} lines`}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <LabDataKpiCard
            label="Period"
            value={`${filters?.from ?? ''} → ${filters?.to ?? ''}`}
            subtitle={summary?.currency ? `Currency: ${summary.currency}` : 'NIS'}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <LabDataKpiCard
            label="Generated"
            value={summary?.generated_at ? new Date(summary.generated_at).toLocaleString() : '—'}
          />
        </Grid>
      </Grid>
      <SpendOverTimeChart periods={periods} />
      <SpendBySupplierTable
        suppliers={suppliers}
        dateFrom={filters?.from}
        dateTo={filters?.to}
      />
    </>
  );
};

const LabDataSpendPage = () => (
  <LabDataLayout title="Lab data — Spend overview" showSpendFilters>
    <LabDataSpendContent />
  </LabDataLayout>
);

export default LabDataSpendPage;
